/**
 * Turning Copilot codes and keys into words on screen, in the user's language.
 */
import type { TFunction, i18n as I18n } from 'i18next';
import type { CopilotProposal, CopilotSuggestion, CopilotToolEvent } from './copilotApi';
import { COPILOT_ERROR_CODES } from './copilotApi';
import { formatLocaleDate } from '../../../i18n/format';

const NS = 'copilot';

/** "copilot:tool.x", "copilot.tool.x" and "tool.x" all name the same key in the copilot namespace. */
export function copilotKey(key: string): string {
  return key.replace(/^copilot[:.]/, '');
}

function has(i18n: I18n, key: string): boolean {
  return i18n.exists(key, { ns: NS });
}

/** A whole number from labelParams, or the tool's default when the model left it out. */
function intParam(params: Record<string, unknown>, name: string, fallback: number): number {
  const v = params[name];
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^-?\d+$/.test(v) ? Number(v) : NaN;
  return Number.isInteger(n) ? n : fallback;
}

/** "today", "tomorrow", "yesterday", "3 days from now", "2 days ago", in the user's language. */
export function dayWords(offset: number, t: TFunction): string {
  if (offset === 0) return String(t('day.today'));
  if (offset === 1) return String(t('day.tomorrow'));
  if (offset === -1) return String(t('day.yesterday'));
  return offset > 0 ? String(t('day.inDays', { count: offset })) : String(t('day.daysAgo', { count: -offset }));
}

/**
 * The translated line for tools whose labelParams change the wording (the server's own defaults apply when a
 * value is missing: day offsets 0, follow-ups 7 days ahead). Null when the tool has no such variant.
 */
function paramLine(name: string, params: Record<string, unknown>, t: TFunction, i18n: I18n): string | null {
  switch (name) {
    case 'get_appointments':
    case 'find_free_slots': {
      const from = intParam(params, 'from_offset_days', 0);
      const to = intParam(params, 'to_offset_days', from);
      const key = from === to ? `tool.${name}_day` : `tool.${name}_range`;
      if (!has(i18n, key)) return null;
      return String(t(key, { day: dayWords(from, t), from: dayWords(Math.min(from, to), t), to: dayWords(Math.max(from, to), t) }));
    }
    case 'get_cash_close': {
      if (!has(i18n, 'tool.get_cash_close_day')) return null;
      return String(t('tool.get_cash_close_day', { day: dayWords(intParam(params, 'day_offset', 0), t) }));
    }
    case 'get_my_followups_due': {
      const days = intParam(params, 'days_ahead', 7);
      if (days <= 0 || !has(i18n, 'tool.get_my_followups_due_days_other')) return null;
      return String(t('tool.get_my_followups_due_days', { count: days }));
    }
    default:
      return null;
  }
}

/**
 * The activity line for one tool call ("Looked up appointments for tomorrow").
 *
 * 1. English: the server's own `label`. It is built from the same inputs as labelParams and is the most specific
 *    line there is (it can name the test, medicine or patient, which labelParams never carry).
 * 2. The server's `labelKey` (`copilot.tool.<name>`) with its `labelParams`: a day/range variant where the
 *    numbers change the wording (appointments, free slots, cash close, follow-ups), else the plain line.
 * 3. Without a known key: the server's `label`, then a generic line.
 */
export function toolLabel(tool: CopilotToolEvent, t: TFunction, i18n: I18n): string {
  const english = (i18n.language ?? 'en').startsWith('en');
  if (english && tool.label) return tool.label;
  const key = tool.labelKey ? copilotKey(tool.labelKey) : `tool.${tool.name}`;
  if (has(i18n, key)) {
    const name = key.startsWith('tool.') ? key.slice('tool.'.length) : tool.name;
    if (tool.labelParams) {
      const line = paramLine(name, tool.labelParams, t, i18n);
      if (line) return line;
    }
    return String(t(key, { ...(tool.labelParams ?? {}) }));
  }
  if (tool.label) return tool.label;
  const generic = `tool.${tool.name}`;
  if (has(i18n, generic)) return String(t(generic));
  return String(t('tool.fallback'));
}

/** A suggested question in the user's language, or null when neither its key nor its text can be shown. */
export function suggestionText(s: CopilotSuggestion, t: TFunction, i18n: I18n): string | null {
  if (s.key) {
    const k = copilotKey(s.key);
    for (const candidate of [`suggest.${k}`, k]) {
      if (has(i18n, candidate)) {
        const v = t(candidate, { ...(s.params ?? {}) });
        if (typeof v === 'string') return v;
      }
    }
  }
  return s.text?.trim() ? s.text : null;
}

const KNOWN = new Set<string>(COPILOT_ERROR_CODES);

/** The friendly sentence for an error code. The server's English message is never shown raw. */
export function errorText(code: string | undefined, t: TFunction): string {
  if (code && KNOWN.has(code)) return String(t(`error.${code}`));
  return String(t('error.generic'));
}

/** Errors worth a "Try again" button: passing conditions, not refusals. */
export function isRetryable(code: string | undefined): boolean {
  return ['CONVERSATION_BUSY', 'RATE_LIMITED', 'INTERNAL', 'AI_UNAVAILABLE', 'BUSY', 'NETWORK', 'INTERRUPTED', 'CANCELLED']
    .includes(code ?? '');
}

/** Confirm / cancel failure codes with their own sentence under `proposalError.` (any other SLOT_* uses SLOT). */
const PROPOSAL_ERROR_CODES = new Set([
  'PROPOSAL_EXPIRED', 'ALREADY_CONFIRMED', 'ALREADY_CANCELLED', 'SLOT_TAKEN', 'CONVERSATION_BUSY', 'NOT_FOUND',
  'NOT_ALLOWED', 'BAD_REQUEST', 'INTERNAL', 'NETWORK', 'SIGNED_OUT',
]);

/**
 * Why a Confirm / Cancel failed, in the user's language.
 *
 * - A code the app knows (expired, already done, slot taken or any other SLOT_*, busy, not found, not allowed,
 *   bad request, internal, network) → its translated sentence; the server's English is not shown.
 * - Any other code is a refusal from the real service (BOOKING_FAILED, REMINDERS_OFF, PATIENT_NOT_FOUND ...). Its
 *   message is written for the user and is the only text that says what went wrong, so it is shown as sent; with
 *   no message, the translated generic sentence.
 */
export function proposalErrorText(code: string | undefined, serverMessage: string | undefined, t: TFunction): string {
  const c = code ?? '';
  if (PROPOSAL_ERROR_CODES.has(c)) return String(t(`proposalError.${c}`));
  if (c.startsWith('SLOT_')) return String(t('proposalError.SLOT'));
  if (serverMessage && serverMessage.trim()) return serverMessage.trim();
  return String(t('proposalError.generic'));
}

/** What a card shows once it is settled. */
export type ProposalDisplayState = 'pending' | 'confirmed' | 'cancelled' | 'expired';

/** True when an ISO `expiresAt` is in the past (an unreadable one is treated as not expired; the server decides). */
export function isPastExpiry(expiresAt: string | undefined, now = Date.now()): boolean {
  if (!expiresAt) return false;
  const at = Date.parse(expiresAt);
  return Number.isFinite(at) && at <= now;
}

/** A card's state from its stored status and expiry: PENDING past expiresAt shows as expired. */
export function proposalDisplayState(p: CopilotProposal, now = Date.now()): ProposalDisplayState {
  switch (p.status) {
    case 'CONFIRMED': return 'confirmed';
    case 'CANCELLED': return 'cancelled';
    case 'EXPIRED': return 'expired';
    default: return isPastExpiry(p.expiresAt, now) ? 'expired' : 'pending';
  }
}

/** The settled line for a card: "Booked", "Sent", "Cancelled. Nothing was changed.", "Expired". */
export function proposalStatusText(state: ProposalDisplayState, action: string, t: TFunction, i18n: I18n): string {
  if (state === 'confirmed') {
    const k = `proposal.done.${action}`;
    return String(t(has(i18n, k) ? k : 'proposal.confirmed'));
  }
  if (state === 'cancelled') return String(t('proposal.cancelled'));
  if (state === 'expired') return String(t('proposal.expired'));
  return '';
}

/** A card's heading: the server's title, else a plain line for its action (stored cards keep no title). */
export function proposalTitle(p: CopilotProposal, t: TFunction, i18n: I18n): string {
  if (p.title && p.title.trim()) return p.title;
  const k = `proposal.actionTitle.${p.action}`;
  return String(t(has(i18n, k) ? k : 'proposal.defaultTitle'));
}

/** Built-in questions per role, used when the server offers no /suggestions (keys under `suggest.`). */
const ROLE_SUGGESTIONS: Record<string, string[]> = {
  doctor: ['myAppointmentsToday', 'pendingResults', 'followUpsDue', 'childParacetamolDose'],
  staff: ['appointmentsTomorrow', 'freeSlotsToday', 'outstandingDues', 'howToRefund'],
  clinic_admin: ['revenueThisMonth', 'noShowsThisWeek', 'outstandingDues', 'staffOnDuty'],
  group_admin: ['revenueThisMonth', 'noShowsThisWeek', 'outstandingDues', 'staffOnDuty'],
  lab_admin: ['labWorklist', 'pendingSignOffs', 'qcToday', 'howToAddLabTest'],
  lab_staff: ['labWorklist', 'pendingSignOffs', 'qcToday', 'howToAddLabTest'],
  lab_assistant: ['labWorklist', 'qcToday', 'howToAddLabTest'],
  pharmacy_admin: ['lowStock', 'expiringSoon', 'salesToday', 'prescriptionsToDispense'],
  pharmacy_staff: ['lowStock', 'expiringSoon', 'prescriptionsToDispense'],
};
const DEFAULT_SUGGESTIONS = ['clinicTimings', 'howToBook', 'whatCanYouDo'];

export function builtInSuggestions(role: string | null | undefined): CopilotSuggestion[] {
  const keys = ROLE_SUGGESTIONS[(role ?? '').toLowerCase()] ?? DEFAULT_SUGGESTIONS;
  return keys.map((key) => ({ key }));
}

/** A proposal detail's label: `detail.<key>` when known, else the key in plain words. */
export function detailLabel(key: string, t: TFunction, i18n: I18n): string {
  const k = `detail.${key}`;
  if (has(i18n, k)) return String(t(k));
  const words = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

/** A detail value as text: lists joined, objects skipped, booleans as yes/no. */
export function detailValue(v: unknown, t: TFunction): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return String(t(v ? 'common.yes' : 'common.no'));
  if (typeof v === 'number' || typeof v === 'string') return String(v);
  if (Array.isArray(v)) {
    const parts = v.map((x) => detailValue(x, t)).filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  return null;
}

// ─── Confirm / Cancel result lines ──────────────────────────────────────────────────────────

/** What a confirm / cancel answered with: the result key and its values, and the server's English line. */
export interface ProposalResult {
  messageKey?: string;
  messageParams?: Record<string, unknown>;
  message?: string;
  summary?: string;
}

/** Keys whose line names a moment (`{{when}}`): built from `at` (date + time) or `date` (date only). */
const RESULT_WHEN_KEYS = new Set([
  'booked', 'booked_walk_in', 'rescheduled', 'rescheduled_walk_in', 'cancelled', 'followup_added',
]);

function textParam(params: Record<string, unknown>, name: string): string | null {
  const v = params[name];
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

/** A yyyy-MM-dd as a local calendar day (never UTC midnight, which can land on the day before). */
function localDay(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole days from today to `d`, in local calendar days. */
function dayOffset(d: Date, now: Date): number {
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / 86_400_000);
}

/** "today" / "tomorrow" / "yesterday", else a short date ("Thu, 30 Oct") in the UI language. */
function dayText(d: Date, t: TFunction, lang: string, now: Date): string {
  const offset = dayOffset(d, now);
  if (offset >= -1 && offset <= 1) return dayWords(offset, t);
  const sameYear = d.getFullYear() === now.getFullYear();
  return formatLocaleDate(d, lang, sameYear
    ? { weekday: 'short', day: 'numeric', month: 'short' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * The moment a result line names, in the UI language: `at` (an ISO instant) as day + time, else `date` (yyyy-MM-dd)
 * as a day, else the server's English `when`. Null when there is none.
 */
export function resultWhen(params: Record<string, unknown>, t: TFunction, lang: string, now = new Date()): string | null {
  const at = textParam(params, 'at');
  if (at) {
    const d = new Date(at);
    if (!Number.isNaN(d.getTime())) {
      const time = formatLocaleDate(d, lang, { hour: 'numeric', minute: '2-digit' });
      return String(t('result.whenAt', { day: dayText(d, t, lang, now), time }));
    }
  }
  const date = textParam(params, 'date');
  const day = date ? localDay(date) : null;
  if (day) return dayText(day, t, lang, now);
  return textParam(params, 'when');
}

/**
 * The line a card shows after Confirm / Cancel, in the user's language.
 *
 * The server's `messageKey` (`copilot.result.*`) with its `messageParams`: the moment is re-formatted from `at` /
 * `date` in the UI language (the English `when` is only a last resort), a missing patient or doctor reads as
 * "the patient" / "the doctor", and a token number adds a " · token #12" ending. With no key, an unknown key or a
 * line that needs a moment the params do not carry, the server's `message`, then `summary`; null when there is
 * nothing to show (the card then shows its plain status).
 */
export function proposalResultText(r: ProposalResult, t: TFunction, i18n: I18n, now = new Date()): string | null {
  const fallback = [r.message, r.summary].find((v) => typeof v === 'string' && v.trim())?.trim() ?? null;
  if (!r.messageKey) return fallback;
  const key = copilotKey(r.messageKey);
  if (!key.startsWith('result.') || !(has(i18n, key) || has(i18n, `${key}_other`))) return fallback;
  const name = key.slice('result.'.length);
  const params = r.messageParams ?? {};
  const lang = i18n.language ?? 'en';
  const values: Record<string, unknown> = {
    patient: textParam(params, 'patient') ?? String(t('result.patientFallback')),
    doctor: textParam(params, 'doctor') ?? String(t('result.doctorFallback')),
  };
  const token = textParam(params, 'token');
  values.tokenPart = token ? String(t('result.tokenPart', { token })) : '';
  let lineKey = key;
  if (RESULT_WHEN_KEYS.has(name)) {
    const when = resultWhen(params, t, lang, now);
    if (when) values.when = when;
    else if (name === 'cancelled' && has(i18n, 'result.cancelled_no_when')) lineKey = 'result.cancelled_no_when';
    else return fallback;
  }
  if (name.startsWith('reorder_drafts')) {
    const n = Number(params.count);
    if (!Number.isFinite(n)) return fallback;
    values.count = n;
  }
  const line = t(lineKey, values);
  return typeof line === 'string' && line.trim() ? line : fallback;
}
